import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SysNotifyService, VendorsService } from '@org/shared-services';
import { VendorRegistrationData } from '@org/interfaces';
import { Router, ActivatedRoute } from '@angular/router';
import { jwtDecode } from 'jwt-decode'; 
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

interface CustomJwtPayload {
  _id: string;
  contactName?: string;
  vendorName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  servicesOffered?: string;
  tenant?: string;
  roles?: string[];
  name?: string;
  email?: string;
  exp?: number;
  iat?: number;
}

@Component({
    selector: 'lib-new-vendor-registration',
    imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective],
    providers: [provideNgxMask()],
    templateUrl: './newVendorRegistration.component.html',
    styleUrl: './newVendorRegistration.component.scss'
})
export class NewVendorRegistrationComponent {
  registrationForm: FormGroup;
  private registrationToken: string | null = null;
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private vendorsService: VendorsService,
    private router: Router,
    private route: ActivatedRoute,
    private sysNotifyService: SysNotifyService
  ) {
    this.registrationForm = this.fb.group({
      tenantName: ['', Validators.required],
      tenantPhoneNumber: ['', Validators.pattern('^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}$')],
      tenantAddress: ['', Validators.required],
      tenantAddress2: [''],
      tenantCity: ['', Validators.required],
      tenantState: ['', Validators.required],
      tenantZip: ['', Validators.required],
      tenantWebsite: [''],
      serviceOffered: [''],
      userName: ['', Validators.required],
      phoneNumber: ['', Validators.pattern('^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}$')],
      userEmail: ['', [Validators.required, Validators.email]],
      userPassword: ['', [Validators.required, Validators.minLength(12)]],
      userPasswordConfirm: ['', [Validators.required, Validators.minLength(12)]]
    }, { validator: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['token']) {
        try {
          const decodedToken = jwtDecode<CustomJwtPayload>(params['token']);
          
          if (decodedToken.exp && decodedToken.iat) {
            const timeSinceCreation = decodedToken.exp - decodedToken.iat;
            const maxAllowedTime = 24 * 60 * 60; // 24 hours in seconds

            if (timeSinceCreation > maxAllowedTime || Date.now() / 1000 > decodedToken.exp) {
              this.sysNotifyService.showError('Registration link has expired. Please request a new one.');
              this.router.navigate(['/expired-registration']);
              return;
            }
          }

          this.registrationToken = params['token'];
          
          this.registrationForm.patchValue({
            userName: decodedToken.contactName || '',
            tenantName: decodedToken.vendorName || '',
            userEmail: decodedToken.emailAddress || '',
            phoneNumber: decodedToken.phoneNumber || '',
            serviceOffered: decodedToken.servicesOffered || ''
          });
        } catch (error) {
          console.error('Error decoding JWT token:', error);
          this.sysNotifyService.showError('Invalid registration token');
          this.router.navigate(['/invalid-registration']);
        }
      } else {
        // No token provided
        this.sysNotifyService.showError('No registration token provided');
        this.router.navigate(['/invalid-registration']);
      }
    });
  }

  passwordMatchValidator(form: FormGroup): any {
    return form.get('userPassword')?.value === form.get('userPasswordConfirm')?.value
      ? null : { mismatch: true };
  }

  getPasswordError(): string {
    const control = this.registrationForm.get('userPassword');
    if (control?.errors) {
      if (control.errors['required']) {
        return 'Password is required';
      }
      if (control.errors['minlength']) {
        return 'Password must be at least 12 characters long';
      }
    }
    return '';
  }

  getFieldError(fieldName: string): string {
    const control = this.registrationForm.get(fieldName);
    if (control?.touched && control?.errors) {
      if (control.errors['required']) {
        return 'This field is required';
      }
      if (control.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (control.errors['minlength']) {
        return `Minimum length is ${control.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  getPasswordMatchError(): string {
    if (this.registrationForm.errors?.['mismatch'] && 
        this.registrationForm.get('userPasswordConfirm')?.touched) {
      return 'Passwords do not match';
    }
    return '';
  }

  onSubmit(): void {
    if (!this.registrationToken) {
      this.sysNotifyService.showError('Registration token is missing');
      return;
    }

    if (this.isSubmitting) {
      return; // Prevent multiple submissions
    }

    if (this.registrationForm.valid) {
      this.isSubmitting = true;

      const tenantData = {
        name: this.registrationForm.get('tenantName')?.value,
        phoneNumber: this.registrationForm.get('tenantPhoneNumber')?.value,
        address: this.registrationForm.get('tenantAddress')?.value,
        address2: this.registrationForm.get('tenantAddress2')?.value,
        city: this.registrationForm.get('tenantCity')?.value,
        state: this.registrationForm.get('tenantState')?.value,
        zip: this.registrationForm.get('tenantZip')?.value,
        serviceOffered: this.registrationForm.get('serviceOffered')?.value
      };

      const userData = {
        name: this.registrationForm.get('userName')?.value,
        email: this.registrationForm.get('userEmail')?.value,
        password: this.registrationForm.get('userPassword')?.value
      };

      const registrationData: VendorRegistrationData = {
        tenant: tenantData,
        user: userData
      };

      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(this.registrationToken);
        
        if (decodedToken.exp && decodedToken.iat) {
          const timeSinceCreation = decodedToken.exp - decodedToken.iat;
          const maxAllowedTime = 24 * 60 * 60; // 24 hours in seconds

          if (timeSinceCreation > maxAllowedTime || Date.now() / 1000 > decodedToken.exp) {
            this.sysNotifyService.showError('Registration link has expired. Please request a new one.');
            this.router.navigate(['/expired-registration']);
            this.isSubmitting = false;
            return;
          }
        }

        this.vendorsService.registerVendor(registrationData, this.registrationToken)
          .subscribe({
            next: () => {
              this.sysNotifyService.showSuccess('Registration successful! You can now log in.');
              this.router.navigate(['/login']);
            },
            error: (error) => {
              this.isSubmitting = false;
              if (error.status === 401) {
                this.sysNotifyService.showError('Registration link has expired or is invalid.');
                this.router.navigate(['/expired-registration']);
              } else {
                this.sysNotifyService.showError('Registration failed. Please try again:' + error);
              }
            }
          });
      } catch (error) {
        this.isSubmitting = false;
        this.sysNotifyService.showError('Invalid registration token.');
        this.router.navigate(['/expired-registration']);
      }
    } else {
      this.sysNotifyService.showError('Please fill in all required fields correctly.');
    }
  }
}
